import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  ogImage?: string;
  ogType?: string;
  canonical?: string;
  schema?: object;
}

export default function SEO({
  title,
  description,
  keywords,
  ogImage = '/images/og-image.jpg',
  ogType = 'website',
  canonical,
  schema
}: SEOProps) {
  const location = useLocation();
  // Always use non-www version as canonical
  const baseUrl = 'https://letsfaceitcosmetics.com';
  const fullUrl = canonical || `${baseUrl}${location.pathname}`;
  const fullTitle = title.includes('Let\'s Face It') ? title : `${title} | Let's Face It Studio`;

  useEffect(() => {
    // Update title
    document.title = fullTitle;

    // Update or create meta tags
    const updateMetaTag = (selector: string, content: string) => {
      let element = document.querySelector(selector) as HTMLMetaElement;
      if (element) {
        element.content = content;
      } else {
        element = document.createElement('meta');
        const [attr, value] = selector.replace(/[[\]]/g, '').split('=');
        element.setAttribute(attr, value.replace(/['"]/g, ''));
        element.content = content;
        document.head.appendChild(element);
      }
    };

    // Basic meta tags
    updateMetaTag('[name="description"]', description);
    if (keywords) {
      updateMetaTag('[name="keywords"]', keywords);
    }

    // Open Graph tags
    updateMetaTag('[property="og:title"]', fullTitle);
    updateMetaTag('[property="og:description"]', description);
    updateMetaTag('[property="og:url"]', fullUrl);
    updateMetaTag('[property="og:image"]', `${baseUrl}${ogImage}`);
    updateMetaTag('[property="og:type"]', ogType);

    // Twitter tags
    updateMetaTag('[name="twitter:title"]', fullTitle);
    updateMetaTag('[name="twitter:description"]', description);
    updateMetaTag('[name="twitter:image"]', `${baseUrl}${ogImage}`);

    // Canonical tag
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (canonicalLink) {
      canonicalLink.href = fullUrl;
    } else {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      canonicalLink.href = fullUrl;
      document.head.appendChild(canonicalLink);
    }

    // Schema.org JSON-LD
    if (schema) {
      let schemaScript = document.querySelector('script[type="application/ld+json"]') as HTMLScriptElement;
      if (!schemaScript) {
        schemaScript = document.createElement('script');
        schemaScript.type = 'application/ld+json';
        document.head.appendChild(schemaScript);
      }
      schemaScript.textContent = JSON.stringify(schema);
    }

    // Cleanup function
    return () => {
      // Remove schema script on unmount to prevent duplicates
      const schemaScript = document.querySelector('script[type="application/ld+json"]');
      if (schemaScript) {
        schemaScript.remove();
      }
    };
  }, [fullTitle, description, keywords, fullUrl, ogImage, ogType, schema]);

  return null;
}
